import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TaskPriority } from '@business/constants/task';
import { UserId, UserResponse } from '@business/entities/account';
import { TaskId, TaskResponse } from '@business/entities/task';
import { TaskStatusResponse } from '@business/entities/task_status';
import { CommonService, UserService, TaskService } from '@business/services';
import { FormCheckbox, FormDropdownDate, FormInput } from '@core/components/form';
import { FormUserAvatar } from '@core/components/form/FormUserAvatar';
import { useHistory } from '@core/router';
import { formControl, formGroup } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useCanHover } from '@core/utils/hooks/dom';
import { toMoment } from '@core/utils/time';
import { Session } from '@modules/auth/session';
import { PrioritySelector } from '@modules/common/PrioritySelector';
import { TableActions } from '@modules/common/TableActions';
import { TaskStatusSelector } from '@modules/common/TaskStatusSelector';

import { Button } from 'reactstrap';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import { R } from '@core/utils/r';

function defineTaskForm() {
  return formGroup({
    checked: formControl<boolean>({}),
    due: formControl<number>({}),
    title: formControl<string>({}),
    assignee: formControl<UserId[]>({}),
  });
}
export interface TaskItemProps {
  task: TaskResponse;
  users: UserResponse[];
  onRemove(id: TaskId): void;
  onUpdate(id: TaskId, task: Partial<TaskResponse>): void;
  onComplete(id: TaskId, completed: boolean): void;
  isCompleted: boolean;
  parentSectionId: string;
  draggable?: boolean;
  taskStatuses: TaskStatusResponse[];
  listeners?: any;
  isSubtask?: boolean;
  parentTitle?: string;
  showSubtasks?: boolean;
}

export const TaskItem = (props: TaskItemProps) => {
  const { task, onUpdate, onRemove, onComplete, isCompleted, parentSectionId, draggable = true, taskStatuses } = props;
  const mNow = toMoment().unix();
  const [isHover, setHover] = useState(false);
  const canHover = useCanHover();
  const form = useMemo(() => defineTaskForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const [isEdit, setEdit] = useState(false);
  const history = useHistory();
  const user = useObservable(Session.user$);

  // Check if task is overdue (for styling)
  const isTaskOverdue = useMemo(() => {
    if (isCompleted) return false;
    return TaskService.isParentTaskOverdue(task);
  }, [task, isCompleted]);

  const isSubtaskOverdue = useMemo(() => {
    if (isCompleted) return false;
    return !task.isCompleted && TaskService.isTaskOverdue(task.dueDate);
  }, [task, isCompleted]);

  useEffect(() => {
    form.patchValue({ checked: task.isCompleted, due: task.dueDate, assignee: task.assignee, title: task.title });
  }, [form, task]);

  const DragHandle = SortableHandle(() => <i className="bx bx-grid-vertical font-size-18 pl-2" style={{ cursor: 'pointer' }} />);

  const onPriority = useCallback(
    (priority: TaskPriority) => {
      onUpdate(task.id, { ...task, priority });
    },
    [onUpdate, task],
  );

  const onClick = useCallback(
    (taskId: TaskId) => {
      history.push(`/todos/${isCompleted ? 'completed' : 'pending'}/task/${taskId}`);
    },
    [history, isCompleted],
  );

  const { isSubtask = false, parentTitle, showSubtasks = false } = props;

  return (
    <div className="d-flex flex-column w-100">
      <div
        className="task-item noselect task-tr-div"
        onMouseMove={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          height: task.organization ? 60 : 50,
          marginLeft: isSubtask ? 30 : 0,
          borderLeft: isSubtask ? '3px solid #e9ecef' : 'none',
          backgroundColor: isSubtask ? '#f8f9fa' : 'transparent',
          marginBottom: isSubtask ? 10 : 0,
          width: isSubtask ? 'calc(100% - 30px)' : '100%',
        }}
      >
        <div className="task-td-div task-w-move">{isHover && draggable && !isCompleted && !isSubtask && <DragHandle />}</div>

        <div className="task-td-div task-w-name">
          <div className="d-flex align-items-center" style={{ width: '100%' }}>
            <FormCheckbox noPadding control={form.controls.checked} onChange={value => onComplete(task.id, value)} />
            {isEdit && (
              <div className="d-flex flex-row align-items-center">
                <FormInput
                  autoFocus
                  control={form.controls.title}
                  withoutGroup
                  onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                    if (event.keyCode === 13) {
                      onUpdate(task.id, { id: task?.id, title: formValue.title });
                      setEdit(false);
                    }
                  }}
                />

                <Button
                  size="sm"
                  outline
                  className="ml-2"
                  onClick={() => {
                    onUpdate(task.id, { id: task?.id, title: formValue.title });
                    setEdit(false);
                  }}
                >
                  Save
                </Button>
                <a onClick={() => setEdit(false)}>
                  <i className="ml-2 fa fa-times p-2"></i>
                </a>
              </div>
            )}
            {!isEdit && (
              <div className="text-truncate">
                <a className="d-flex flex-column" onClick={() => onClick(task.id)}>
                  <span
                    className={`text-truncate ${
                      isSubtask
                        ? isSubtaskOverdue
                          ? 'font-size-14 text-danger font-weight-bold'
                          : 'font-size-14'
                        : isTaskOverdue
                        ? 'text-danger font-weight-bold'
                        : 'text-dark'
                    }`}
                  >
                    {isSubtask && <span className="text-muted mr-2">└</span>}
                    {task.title}
                  </span>
                  <span
                    style={{ fontSize: 10 }}
                    className={`${
                      isSubtask
                        ? isSubtaskOverdue
                          ? 'text-danger font-weight-bold'
                          : 'text-muted'
                        : isTaskOverdue
                        ? 'text-danger font-weight-bold'
                        : 'text-muted'
                    }`}
                  >
                    {task.organization}
                  </span>
                </a>
                {task.isCompleted && task.completedBy && (
                  <div className="text-muted font-size-10">
                    <b>{CommonService.getFullName(task.completedBy)}</b> {toMoment(task.completedAt!, { utc: true }).fromNow()}
                  </div>
                )}
              </div>
            )}
            {!isSubtask && (
              <span className="text-muted ml-3 font-size-12" style={{ opacity: 0.8 }}>
                {task.children.length > 0 && (
                  <span>
                    <i className="fa fa-sitemap mr-1"></i>
                    <span style={{ fontWeight: '600' }}>{`${task.children.filter(f => f.isCompleted).length}/${task.children.length}`}</span>
                  </span>
                )}
                {task.comments > 0 && (
                  <span className={task.children.length > 0 ? 'ml-2' : 'ml-2'}>
                    <i className="fa fa-comment mr-1"></i>
                    <span style={{ fontWeight: '600' }}>{task.comments + R.reduce(task.children, (total, item) => item.comments + total, 0)}</span>
                  </span>
                )}
              </span>
            )}

            {(isHover || !canHover) && !isEdit && (
              <TableActions style={{ marginLeft: 10 }} onEdit={() => setEdit(true)} onDelete={() => onRemove(task.id)} />
            )}
          </div>
        </div>
        <div className="task-td-div task-w-assignee">
          <FormUserAvatar
            tooltipContainer={parentSectionId}
            control={form.controls.assignee}
            users={props.users}
            meId={user?.id}
            onChange={assignee => assignee && onUpdate(task.id, { ...task, assignee })}
            addMe={false}
          />
        </div>
        {(!!taskStatuses.length || UserService.isAdmin(Session.user)) && (
          <div className="task-td-div task-w-assignee">
            <TaskStatusSelector
              statuses={taskStatuses}
              item={task.status}
              onSelect={status => onUpdate(task.id, { ...task, statusId: status ? status.id : '0' })}
            />
          </div>
        )}
        <div className="task-td-div task-w-due">
          <FormDropdownDate
            clearable
            control={form.controls.due}
            onChange={dueDate => {
              onUpdate(task.id, { ...task, dueDate });
            }}
          >
            <div className={`d-flex ml-2 ${TaskService.isTaskOverdue(task.dueDate) ? 'text-danger font-weight-bold' : ''}`}>
              {!!task.dueDate && (
                <div className="d-flex flex-column align-items-start">
                  <span>{TaskService.formatTaskDueDate(task.dueDate)}</span>
                  {TaskService.hasSpecificTime(task.dueDate) && (
                    <small className="text-muted" style={{ fontSize: '10px', marginTop: '-2px' }}>
                      Due time set
                    </small>
                  )}
                </div>
              )}
              {!task.dueDate && <i className="bx bx-calendar font-size-16 text-primary"></i>}
            </div>
          </FormDropdownDate>
        </div>
        <div className="task-td-div task-w-priority">
          <PrioritySelector priority={task.priority} onSelect={priority => onPriority(priority)} />
        </div>
      </div>

      {/* Render subtasks with proper hierarchy */}
      {!isSubtask && showSubtasks && task.children.length > 0 && (
        <>
          {task.children.map(subtask => (
            <TaskItem
              key={subtask.id}
              task={subtask}
              users={props.users}
              onRemove={onRemove}
              onUpdate={onUpdate}
              onComplete={onComplete}
              isCompleted={isCompleted}
              parentSectionId={parentSectionId}
              draggable={false}
              taskStatuses={taskStatuses}
              isSubtask={true}
              parentTitle={task.title}
              showSubtasks={false}
            />
          ))}
        </>
      )}
    </div>
  );
};

export const SortableTaskItem = SortableElement(TaskItem);
